/**
 * Contiguous rebuild of the paginated chat transcript (PR #165 I1).
 */
import type { MessagePage } from '../../services/chatApi';

export type ChatPagesState = {
  pages: MessagePage[];
  pageParams: (number | null)[];
};

/**
 * Refetch the loaded page stack with FRESH, derived cursors.
 *
 * React Query refetches infinite pages with their ORIGINAL pageParams: after
 * a new message lands, page 1 re-anchors to the newest window while older
 * pages keep their frozen cursors — the pages stop being contiguous and the
 * flattened transcript grows a seq hole (unreachable: the refetched oldest
 * page then reports has_more=false, so "Load more" disappears too).
 *
 * This fetches each page in order, deriving every cursor from the page before
 * it, so the stack is contiguous at any depth. Server-truth on every rebuild
 * — no client-side append bookkeeping to drift.
 */
export async function buildContiguousPages(
  fetchPage: (beforeSeq?: number) => Promise<MessagePage>,
  depth: number
): Promise<ChatPagesState> {
  const pages: MessagePage[] = [];
  const pageParams: (number | null)[] = [];
  let cursor: number | undefined;
  for (let i = 0; i < depth; i++) {
    const page = await fetchPage(cursor);
    pages.push(page);
    pageParams.push(cursor ?? null);
    if (!page.has_more || page.messages.length === 0) break;
    const oldest = page.messages[0].seq;
    if (oldest == null) break;
    cursor = oldest;
  }
  return { pages, pageParams };
}

/**
 * Chronological message list from a page stack.
 *
 * Pages are stored newest-first (page 1 = the latest window, page 2 = older,
 * …) while the transcript renders oldest→newest — reverse the page order
 * before flattening. (With 2+ pages loaded, a bare `pages.flatMap` renders
 * the older block *below* the newer one.)
 */
export function flattenPages(state: { pages: MessagePage[] } | undefined): MessagePage['messages'] {
  if (!state?.pages) return [];
  return [...state.pages].reverse().flatMap((p) => p.messages);
}
